import { createContext, useContext, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LeadForm } from "@/components/LeadForm";
import { QuoteEstimator } from "@/components/QuoteEstimator";
import { BookingWizard } from "@/components/BookingWizard";
import { getSavedCart } from "@/lib/savedCart";

const LeadModalContext = createContext(null);
export const useLeadModal = () => useContext(LeadModalContext);

export const LeadModalProvider = ({ children }) => {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingPrefill, setBookingPrefill] = useState(null);
  const [proposalOpen, setProposalOpen] = useState(false);

  const openQuote = useCallback(() => setQuoteOpen(true), []);
  const openBooking = useCallback(() => { setBookingPrefill(null); setBookingOpen(true); }, []);
  const resumeBooking = useCallback(() => {
    const saved = getSavedCart();
    setBookingPrefill(saved ? { ...saved, date: saved.date ? new Date(saved.date) : null } : null);
    setBookingOpen(true);
  }, []);
  const openProposal = useCallback(() => setProposalOpen(true), []);

  return (
    <LeadModalContext.Provider value={{ openQuote, openBooking, resumeBooking, openProposal, anyModalOpen: quoteOpen || bookingOpen || proposalOpen }}>
      {children}

      {/* Get Instant Quote — estimator popup */}
      <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
        <DialogContent className="max-w-lg p-0 gap-0 bg-background border-border flex flex-col max-h-[90vh] max-h-[90dvh]" data-lenis-prevent data-testid="quote-modal">
          <DialogHeader className="px-6 pt-6 pb-3 shrink-0">
            <DialogTitle className="font-heading text-xl sm:text-2xl text-navy tracking-tight">Get Your Instant Quote</DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">See a price in seconds — Green Cleaning included.</DialogDescription>
          </DialogHeader>
          {quoteOpen && (
            <QuoteEstimator
              mode="quote"
              onClose={() => setQuoteOpen(false)}
              onBook={(payload) => { setBookingPrefill(payload || null); setQuoteOpen(false); setTimeout(() => setBookingOpen(true), 160); }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Book Online — multi-step booking wizard (drawer on mobile, dialog on desktop) */}
      <BookingWizard open={bookingOpen} onOpenChange={setBookingOpen} prefill={bookingPrefill} />

      {/* Request a Proposal popup */}
      <Dialog open={proposalOpen} onOpenChange={setProposalOpen}>
        <DialogContent className="max-w-lg bg-background border-border max-h-[92vh] overflow-y-auto" data-lenis-prevent data-testid="lead-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-navy tracking-tight">Request a Commercial Quote</DialogTitle>
            <DialogDescription className="text-muted-foreground">Every facility receives a tailored cleaning plan built around your schedule and needs.</DialogDescription>
          </DialogHeader>
          <LeadForm type="proposal" onSuccess={() => setProposalOpen(false)} />
        </DialogContent>
      </Dialog>
    </LeadModalContext.Provider>
  );
};
